import { useCallback, useMemo } from "react";
import { oids } from "@/configs/Settings";
import { useSessionContext } from "@/contexts/SessionContext";
import { inviteAppointmentId } from "../../invites/helpers/inviteTaskHelpers";
import { usePendingInviteTasks } from "../../invites/hooks/usePendingInviteTasks";
import {
  isSamePractitionerReference,
  teleconsultoriaGuestOfAppointment,
} from "../../utils/actors";
import type { BookedTeleconsultoriaRow } from "../types";

/**
 * Ações do TELECONSULTOR logado em cada linha:
 * - `canStart`: ele é o convidado do Appointment (CON ou, sem marcação, a ordem),
 *   comparado pelo CPF (o formato da reference pode variar);
 * - `pendingInviteOf`: convite ainda não respondido para aquela consulta
 *   (vem do contexto de tasks/SSE), para aceitar/recusar ali mesmo.
 */
export function useTeleconsultorRowActions() {
  const { user } = useSessionContext();
  const myPractitionerReference = `Practitioner/${oids.cpf}-${user?.username}`;
  const pendingInvites = usePendingInviteTasks();
  const pendingByAppointment = useMemo(
    () => new Map(pendingInvites.map((task) => [inviteAppointmentId(task), task])),
    [pendingInvites],
  );

  const canStart = useCallback(
    (row: BookedTeleconsultoriaRow) =>
      isSamePractitionerReference(
        teleconsultoriaGuestOfAppointment(row.appointment).practitioner?.reference,
        myPractitionerReference,
      ),
    [myPractitionerReference],
  );
  const pendingInviteOf = useCallback(
    (row: BookedTeleconsultoriaRow) => pendingByAppointment.get(row.appointmentId),
    [pendingByAppointment],
  );

  return { canStart, pendingInviteOf };
}
